import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle, Send, CheckCircle, XCircle, Clock, Phone, RefreshCw, Search } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const WhatsAppPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSend, setShowSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [logsRes, statusRes] = await Promise.all([
        axios.get(`${API}/whatsapp/logs?limit=100`),
        axios.get(`${API}/whatsapp/status`),
      ]);
      setLogs(logsRes.data);
      setStatus(statusRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const sendMessage = async () => {
    if (!phone.trim() || !message.trim()) { toast.error('Numero et message requis'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/whatsapp/send-text`, { phone: phone.trim(), message: message.trim() });
      if (res.data.success) {
        toast.success('Message WhatsApp envoye !');
        setShowSend(false);
        setPhone(''); setMessage('');
        loadData();
      } else {
        toast.error(res.data.error || 'Erreur envoi');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSending(false); }
  };

  const filteredLogs = search
    ? logs.filter(l => l.phone?.includes(search) || l.message?.toLowerCase().includes(search.toLowerCase()) || l.type?.includes(search))
    : logs;

  const successCount = logs.filter(l => l.result === 'success').length;
  const errorCount = logs.filter(l => l.result === 'error').length;

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto" data-testid="whatsapp-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="whatsapp-title">WhatsApp Business</h1>
              <p className="text-sm text-slate-500">Notifications et messages clients</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {status && (
              <Badge className={status.configured ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} data-testid="wa-status">
                {status.configured ? 'Connecte' : 'Non configure'}
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-logs">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={() => setShowSend(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-message-btn">
              <Send className="w-4 h-4 mr-1" /> Nouveau message
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="p-4 border-l-4 border-l-slate-400">
            <p className="text-xs text-slate-500 uppercase">Total envoyes</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{logs.length}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-emerald-400">
            <p className="text-xs text-slate-500 uppercase">Reussis</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1">{successCount}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-red-400">
            <p className="text-xs text-slate-500 uppercase">Erreurs</p>
            <p className="text-2xl font-bold text-red-700 mt-1">{errorCount}</p>
          </Card>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par numero, message, type..."
            className="pl-10" data-testid="search-logs" />
        </div>

        {/* Logs */}
        <Card className="overflow-hidden" data-testid="whatsapp-logs">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="px-4 py-3 text-left font-medium">Date</th>
                  <th className="px-4 py-3 text-left font-medium">Type</th>
                  <th className="px-4 py-3 text-left font-medium">Destinataire</th>
                  <th className="px-4 py-3 text-left font-medium">Message</th>
                  <th className="px-4 py-3 text-center font-medium">{t('common.status')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-400">{t('common.loading')}</td></tr>
                ) : filteredLogs.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-400">Aucun message envoye</td></tr>
                ) : filteredLogs.map((log, idx) => (
                  <tr key={log.id} className={`border-b border-slate-100 ${idx % 2 ? 'bg-slate-50/50' : 'bg-white'}`}
                    data-testid={`log-${log.id}`}>
                    <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString('fr-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge className={
                        log.type === 'vehicle_ready' ? 'bg-blue-100 text-blue-700' :
                        log.type === 'appointment_reminder' ? 'bg-violet-100 text-violet-700' :
                        log.type === 'template' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                      }>
                        {log.type === 'vehicle_ready' ? 'Vehicule pret' :
                         log.type === 'appointment_reminder' ? 'Rappel RDV' :
                         log.type === 'template' ? 'Template' : 'Texte'}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-600">+{log.phone}</td>
                    <td className="px-4 py-2.5 text-slate-600 max-w-xs truncate">{log.message || log.template_name || '-'}</td>
                    <td className="px-4 py-2.5 text-center">
                      {log.result === 'success' ? (
                        <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                      ) : (
                        <span className="flex items-center justify-center gap-1">
                          <XCircle className="w-4 h-4 text-red-500" />
                          <span className="text-[10px] text-red-500 max-w-[120px] truncate">{log.error}</span>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Send Message Dialog */}
      <Dialog open={showSend} onOpenChange={setShowSend}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-emerald-500" /> Envoyer un message WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Numero de telephone</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+41 XX XXX XX XX"
                  className="pl-10" data-testid="wa-phone-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Message</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)}
                placeholder="Votre message..."
                className="w-full rounded-md border border-slate-200 p-3 text-sm min-h-[120px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="wa-message-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSend(false)}>{t('common.cancel')}</Button>
            <Button onClick={sendMessage} disabled={sending || !phone.trim() || !message.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wa-send-btn">
              {sending ? 'Envoi...' : 'Envoyer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default WhatsAppPage;
